import { useMemo, useState } from 'react'
import type { JSX } from 'react'
import { DETECT_DEFAULTS } from '../api/client'
import { describeError } from '../api/errors'
import type { AudioTrack, DetectLinesParams, DetectedSegment, Role } from '../api/types'
import { formatTime } from '../lib/format'
import { TRACK_KIND_SHORT, trackTitle } from '../lib/tracks'
import { Modal } from './Modal'

// ------------------------------------------------------------------ диалог

export type DetectLinesDialogProps = {
  tracks: AudioTrack[]
  /** Бросает — диалог покажет текст ошибки и останется открытым. */
  onRun: (params: DetectLinesParams) => Promise<DetectedSegment[]>
  onDetected: (segments: DetectedSegment[]) => void
  onClose: () => void
}

type NumberFieldProps = {
  id: string
  label: string
  hint: string
  value: number
  step: number
  min: number
  max: number
  disabled: boolean
  onChange: (value: number) => void
}

function NumberField({
  id,
  label,
  hint,
  value,
  step,
  min,
  max,
  disabled,
  onChange,
}: NumberFieldProps): JSX.Element {
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        value={value}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => {
          const next = Number(e.currentTarget.value)
          if (Number.isFinite(next)) onChange(next)
        }}
      />
      <span className="faint">{hint}</span>
    </div>
  )
}

export function DetectLinesDialog({
  tracks,
  onRun,
  onDetected,
  onClose,
}: DetectLinesDialogProps): JSX.Element {
  const preferred = useMemo(() => {
    const dialogue = tracks.find((track) => track.kind === 'dialogue')
    if (dialogue) return dialogue.id
    const full = tracks.find((track) => track.kind === 'full')
    return full?.id ?? tracks[0]?.id ?? ''
  }, [tracks])

  const [trackId, setTrackId] = useState(preferred)
  const [threshold, setThreshold] = useState<number>(DETECT_DEFAULTS.threshold)
  const [minSilence, setMinSilence] = useState<number>(DETECT_DEFAULTS.minSilence)
  const [minLine, setMinLine] = useState<number>(DETECT_DEFAULTS.minLine)
  const [padding, setPadding] = useState<number>(DETECT_DEFAULTS.padding)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selected = tracks.find((track) => track.id === trackId) ?? null

  const run = async (): Promise<void> => {
    if (!trackId) return
    setRunning(true)
    setError(null)
    try {
      const segments = await onRun({ trackId, threshold, minSilence, minLine, padding })
      if (segments.length === 0) {
        setError('Сегменты не найдены. Поднимите порог тишины или уменьшите минимальную реплику.')
        return
      }
      onDetected(segments)
    } catch (err) {
      setError(describeError(err))
    } finally {
      setRunning(false)
    }
  }

  return (
    <Modal
      title="Разметить автоматически"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" onClick={onClose} disabled={running}>
            Отмена
          </button>
          <span className="spacer" />
          <button
            type="button"
            className="primary"
            disabled={running || !trackId}
            onClick={() => void run()}
          >
            {running ? 'Ищу сегменты…' : 'Найти сегменты'}
          </button>
        </>
      }
    >
      {tracks.length === 0 ? (
        <p className="muted">
          Дорожек нет. Разметка идёт по чистой дорожке реплик: на смешанном звуке детектор тишины
          забивается музыкой.
        </p>
      ) : (
        <>
          <div>
            <label htmlFor="detect-track">Дорожка</label>
            <select
              id="detect-track"
              value={trackId}
              disabled={running}
              onChange={(e) => setTrackId(e.currentTarget.value)}
            >
              {tracks.map((track) => (
                <option key={track.id} value={track.id}>
                  {trackTitle(track)} · {TRACK_KIND_SHORT[track.kind]} · {formatTime(track.duration)}
                </option>
              ))}
            </select>
            {selected && selected.kind !== 'dialogue' ? (
              <span className="faint">
                Это не дорожка реплик — на музыке и эффектах детектор почти наверняка ошибётся.
              </span>
            ) : null}
          </div>

          <div className="detect-grid">
            <NumberField
              id="detect-threshold"
              label="Порог тишины, дБ"
              hint={`по умолчанию ${DETECT_DEFAULTS.threshold}`}
              value={threshold}
              step={1}
              min={-90}
              max={0}
              disabled={running}
              onChange={setThreshold}
            />
            <NumberField
              id="detect-min-silence"
              label="Минимальная пауза, с"
              hint={`разрыв между репликами, по умолчанию ${DETECT_DEFAULTS.minSilence}`}
              value={minSilence}
              step={0.05}
              min={0.05}
              max={5}
              disabled={running}
              onChange={setMinSilence}
            />
            <NumberField
              id="detect-min-line"
              label="Минимальная реплика, с"
              hint={`короче — отбрасывается, по умолчанию ${DETECT_DEFAULTS.minLine}`}
              value={minLine}
              step={0.05}
              min={0.05}
              max={10}
              disabled={running}
              onChange={setMinLine}
            />
            <NumberField
              id="detect-padding"
              label="Запас на границах, с"
              hint={`добавляется с обеих сторон, по умолчанию ${DETECT_DEFAULTS.padding}`}
              value={padding}
              step={0.05}
              min={0}
              max={2}
              disabled={running}
              onChange={setPadding}
            />
          </div>

          {error ? <p className="notice warn">{error}</p> : null}

          <p className="faint">
            Сервер только вернёт границы фраз — ничего не создаст. Найденное появится
            предпросмотром поверх таймлайна, и уже оттуда его можно применить или отменить.
          </p>
        </>
      )}
    </Modal>
  )
}

// ------------------------------------------------------ подтверждение разметки

export type DetectApplyOptions = { roleId: string; mode: 'replace' | 'append' }

export type DetectPreviewBarProps = {
  segments: DetectedSegment[]
  roles: Role[]
  existingLines: number
  applying: boolean
  onApply: (options: DetectApplyOptions) => void
  onCancel: () => void
}

export function DetectPreviewBar({
  segments,
  roles,
  existingLines,
  applying,
  onApply,
  onCancel,
}: DetectPreviewBarProps): JSX.Element {
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '')
  const [mode, setMode] = useState<'replace' | 'append'>('replace')

  const total = useMemo(
    () => segments.reduce((sum, segment) => sum + Math.max(0, segment.end - segment.start), 0),
    [segments],
  )

  return (
    <div className="detect-bar">
      <div className="row wrap">
        <strong>Предпросмотр разметки: {segments.length} сегм.</strong>
        <span className="faint mono">суммарно {formatTime(total)}</span>
        <span className="faint">
          {segments[0] ? `первый — ${formatTime(segments[0].start)}` : ''}
        </span>
      </div>

      <div className="row wrap">
        <label htmlFor="detect-role" style={{ margin: 0 }}>
          Роль
        </label>
        <select
          id="detect-role"
          value={roleId}
          disabled={applying}
          onChange={(e) => setRoleId(e.currentTarget.value)}
          style={{ width: 'auto', minWidth: 160 }}
        >
          <option value="">Без роли</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>

        <label className="row" style={{ gap: 6, margin: 0, cursor: 'pointer' }}>
          <input
            type="radio"
            name="detect-mode"
            checked={mode === 'replace'}
            disabled={applying}
            onChange={() => setMode('replace')}
          />
          <span style={{ color: 'var(--text)' }}>Заменить реплики ({existingLines})</span>
        </label>
        <label className="row" style={{ gap: 6, margin: 0, cursor: 'pointer' }}>
          <input
            type="radio"
            name="detect-mode"
            checked={mode === 'append'}
            disabled={applying}
            onChange={() => setMode('append')}
          />
          <span style={{ color: 'var(--text)' }}>Добавить к существующим</span>
        </label>

        <span className="spacer" />
        <button type="button" className="ghost" disabled={applying} onClick={onCancel}>
          Отменить
        </button>
        <button
          type="button"
          className="primary"
          disabled={applying}
          onClick={() => onApply({ roleId, mode })}
        >
          {applying ? 'Применяю…' : 'Применить разметку'}
        </button>
      </div>

      {mode === 'replace' && existingLines > 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          Существующие реплики будут удалены вместе с записанными дублями — сервер заменяет сценарий
          целиком.
        </p>
      ) : null}
    </div>
  )
}
